import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import useAppMutation from 'hooks/useAppMutation';
import useAppUser from 'hooks/useAppUser';
import useNotify from 'hooks/useNotify';
import API from 'services/API';
import { getErrorMessage } from 'utils/axiosExceptionUtils';
import { BaseErrorResponse } from 'models/_base';
import {
  ApplicationDnsSetting,
  CreateAgencyDnsSettingPayload,
  CreateAgencyDnsSettingResponse,
} from 'models/CustomDomains';
import { AGENCY_DNS_SETTINGS_BASE_QUERY_KEY } from './useFetchAgencyDnsSettings';
import { postCustomDomainsUpdatedToLegacyFrame } from './customDomains.utils';

const useCreateAgencyDnsSetting = () => {
  const { agencyUid } = useAppUser();
  const queryClient = useQueryClient();
  const { notifyError } = useNotify();
  const { t } = useTranslation();

  const { mutate: createDnsSetting, isLoading: isCreating } = useAppMutation(
    async ({ domain, application }: CreateAgencyDnsSettingPayload) => {
      const response = await API.post<CreateAgencyDnsSettingResponse>(
        'api/internal/agencies/dns-settings',
        {
          agencyUid,
          domain,
          application,
        },
      );
      return response.data.dnsSettings;
    },
    {
      onSuccess: (dnsSetting) => {
        const updatedSettings = queryClient.setQueryData<
          ApplicationDnsSetting[]
        >(
          [AGENCY_DNS_SETTINGS_BASE_QUERY_KEY, agencyUid],
          (currentSettings = []) => [
            ...currentSettings.filter(
              ({ application }) => application !== dnsSetting.application,
            ),
            dnsSetting,
          ],
        );

        postCustomDomainsUpdatedToLegacyFrame(updatedSettings ?? [dnsSetting]);
      },
      onError: (error: BaseErrorResponse) => {
        notifyError(
          getErrorMessage({
            apiErrorCode: error.response?.data?.apiErrorCode,
            apiErrorMessage:
              error.response?.data?.apiErrorMessage ??
              t('componentCustomDomains.saveError'),
            baseKey: 'componentCustomDomains.apiErrorCodes.',
            t,
          }),
        );
      },
    },
  );

  return { createDnsSetting, isCreating };
};

export default useCreateAgencyDnsSetting;
